import React from 'react';
import { Typography } from 'antd';
import type {
  AuthorityLeaseStatusV0,
  AuthorityRequirementViewV0,
} from '@shared/operator-read-models';
import {
  PRIMITIVE_AUTHORITY_FLAGS,
  type PrimitivePresentationState,
} from './primitiveSurfaceState';
import { PrimitiveSurfaceFrame } from './PrimitiveSurfaceFrame';
import styles from './primitives.module.css';

const { Text, Paragraph } = Typography;

export interface AuthoritySummaryProps {
  readonly presentationState: PrimitivePresentationState;
  /** R0 AuthorityRequirementView — never grants authority. */
  readonly requirement?: AuthorityRequirementViewV0 | null;
  /** R0 AuthorityLeaseStatus — usableNow is server-computed. */
  readonly lease?: AuthorityLeaseStatusV0 | null;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

export function AuthoritySummary(props: AuthoritySummaryProps): React.ReactElement {
  const grantsAuthority = props.grantsAuthority ?? PRIMITIVE_AUTHORITY_FLAGS.grantsAuthority;
  const grantsApproval = props.grantsApproval ?? PRIMITIVE_AUTHORITY_FLAGS.grantsApproval;
  if (grantsAuthority !== false || grantsApproval !== false) {
    throw new Error('AuthoritySummary must keep grantsAuthority/grantsApproval=false');
  }

  const requirement = props.requirement;
  const lease = props.lease;

  return (
    <PrimitiveSurfaceFrame
      title="권한 요약"
      presentationState={props.presentationState}
      emptyMessage="권한 요구사항이 없습니다."
      deniedMessage="권한 평가가 거부되었습니다."
      data-testid={props['data-testid'] ?? 'authority-summary'}
      aria-label="권한 요약"
    >
      <div className={styles.primitiveStack}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          UI 상태는 권한이 아닙니다. grantsAuthority=false
        </Paragraph>
        {requirement ? (
          <>
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>필요 역량</span>
              <Text>{requirement.requiredCapability}</Text>
            </div>
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>범위</span>
              <Text>{requirement.authorityScope}</Text>
            </div>
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>Grant</span>
              <Text>
                {requirement.grantRef ?? '없음'} ({requirement.grantStatus})
              </Text>
            </div>
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>결정</span>
              <Text>
                {requirement.decisionStatus}
                {requirement.decisionReasonCode ? ` · ${requirement.decisionReasonCode}` : ''}
              </Text>
            </div>
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>결정 영수증</span>
              <Text>{requirement.decisionReceiptRef ?? '—'}</Text>
            </div>
          </>
        ) : (
          <Text type="secondary">AuthorityRequirementView 없음</Text>
        )}
        {lease ? (
          <>
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>Lease 상태</span>
              <Text>{lease.state}</Text>
            </div>
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>usableNow (서버)</span>
              <Text>{lease.usableNow ? 'true' : 'false'}</Text>
            </div>
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>만료 / 철회</span>
              <Text>
                {lease.expiresAt ?? '—'} / {lease.revokedAt ?? '—'}
              </Text>
            </div>
            {lease.blockingReason ? (
              <div className={styles.primitiveFieldRow}>
                <span className={styles.primitiveFieldLabel}>차단 사유</span>
                <Text>{lease.blockingReason}</Text>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </PrimitiveSurfaceFrame>
  );
}
